import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@api';
import { CartCookies, MergedProduct } from '@types';
import {
  SERVER_ERROR, ALM_COOKIE_KEY, ECOM_COOKIE_KEY, CONNECT_ACCESS_TOKEN_KEY, CONNECT_ID_TOKEN_KEY,
} from '@config/constants';
import { updateCookies, deleteCookie } from '@utils';
import {
  GetCartResponse, GET_CART_QUERY,
  GetCartDiscountsResponse, GET_CART_DISCOUNTS_QUERY,
} from '@requests/cart';
import { introspectToken } from './introspectToken';
import { mergeCarts } from './mergeCarts';

type GetCartDataResult = GetCartResponse & GetCartDiscountsResponse & { mergedProducts: MergedProduct[] };

export const getCartData = createAsyncThunk<GetCartDataResult, CartCookies>(
  'cart/getCartData',
  async (cartCookies, { rejectWithValue }) => {
    if (!cartCookies.cartId) return rejectWithValue('Missed cart ID');

    const uid = cartCookies.cartId;
    let mergedProducts: MergedProduct[] = [];
    try {
      const introspectResult = await introspectToken(
        cartCookies.almToken,
        cartCookies.ecomToken,
        cartCookies.connectAccessToken,
        cartCookies.connectIdToken,
      );
      if (introspectResult.tokenIsValid) {
        updateCookies(cartCookies.almToken, introspectResult.ecomToken);

        mergedProducts = await mergeCarts({ uid, ecomToken: introspectResult.ecomToken! });
      }

      if (!introspectResult.tokenIsValid) {
        deleteCookie(ALM_COOKIE_KEY);
        deleteCookie(ECOM_COOKIE_KEY);
        deleteCookie(CONNECT_ACCESS_TOKEN_KEY);
        deleteCookie(CONNECT_ID_TOKEN_KEY);
      }

      const [cartData, cartDiscounts] = await Promise.all([
        client.request<GetCartResponse>(GET_CART_QUERY, { uid }),
        client.request<GetCartDiscountsResponse>(GET_CART_DISCOUNTS_QUERY, { uid }),
      ]);

      return { ...cartData, ...cartDiscounts, mergedProducts };
    } catch (error) {
      return rejectWithValue(SERVER_ERROR);
    }
  },
);
